import { forwardRef, useMemo } from 'react';
import {
  MolecularSVGProvider,
  SVGCanvas,
  applyZoomToViewBox,
  parseViewBoxSafe,
  viewBoxToString,
} from '@alchemiesolns/svg-interactive';
import { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import { fromConfigString } from '@alchemiesolns/workspace-serialization';
import {
  DEFAULT_MOLECULE_EXPORT_DIMENSIONS,
  MOLECULE_LAYOUT_BASE,
} from '../utils/exportLayout';
import { getSingleMoleculeScaleCompensation } from '../utils/reactionExportLayout';
import { AtomScaleWrap } from './AtomScaleWrap';
import { InteractiveMolecule, type RenderingMetrics } from './InteractiveMolecule';
import { OverlayTextSvg, type OverlayTextHorizontalAlign } from './OverlayTextSvg';
import {
  REACTION_LABEL_BASE_PX,
  REACTION_PREVIEW_FONT_FAMILY,
  reactionPreviewDropTargetHeadingStyle,
} from '../constants/previewTypography';
import {
  SCHEME_DISPLAY_ZOOM_MAX,
  SCHEME_DISPLAY_ZOOM_MIN,
} from '../constants/reactionScheme';
import { schemeViewBoxZoomFactorForApply } from '../utils/schemeZoom';
import { useTapClickSelectSkippingDrag } from '../hooks/useTapClickSelectSkippingDrag';

interface SingleMoleculeExportProps {
  config: string;
  viewBox?: string;
  layoutViewBox?: string;
  persistedViewBox?: string | null;
  overlayText?: string;
  overlayPosition?: { x: number; y: number } | null;
  overlayTextHorizontalAlign?: OverlayTextHorizontalAlign;
  onOverlayPositionChange?: (p: { x: number; y: number }) => void;
  exportWidth?: number;
  exportHeight?: number;
  showMoleculeDropTarget?: boolean;
  onMoleculeDragOver?: (e: React.DragEvent<HTMLDivElement>) => void;
  onMoleculeDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
  onMoleculePaste?: (e: React.ClipboardEvent<HTMLDivElement>) => void;
  selectedMolecule?: boolean;
  onMoleculeSelect?: () => void;
  interactivePreview?: boolean;
  onMoleculeViewBoxChange?: (viewBox: string | null) => void;
  onMoleculeZoomChange?: (zoom: number) => void;
  onMoleculeMetricsChange?: (metrics: RenderingMetrics) => void;
  onMoleculeControlsReady?: (controls: unknown | null) => void;
  schemeDisplayZoom?: number;
  id?: string;
  className?: string;
}

interface BorderlessMoleculeProps {
  config: string;
  width: number;
  height: number;
  viewBoxOverride?: string;
  schemeDisplayZoom?: number;
  overlayText?: string;
  overlayPosition?: { x: number; y: number } | null;
  overlayInteractive?: boolean;
  onOverlayPositionChange?: (p: { x: number; y: number }) => void;
  overlayTextHorizontalAlign?: OverlayTextHorizontalAlign;
}

function BorderlessMolecule({
  config,
  width,
  height,
  viewBoxOverride,
  schemeDisplayZoom = 1,
  overlayText,
  overlayPosition = null,
  overlayInteractive = false,
  onOverlayPositionChange,
  overlayTextHorizontalAlign = 'start',
}: BorderlessMoleculeProps) {
  const workspace = useMemo(() => {
    if (!config) return new MolecularWorkspace();
    try {
      return fromConfigString(config);
    } catch {
      return new MolecularWorkspace();
    }
  }, [config]);

  const resolvedViewBoxForCanvas = useMemo(() => {
    if (!viewBoxOverride) return undefined;
    if (schemeDisplayZoom === 1) return viewBoxOverride;
    const fit = parseViewBoxSafe(viewBoxOverride);
    const z = schemeViewBoxZoomFactorForApply(schemeDisplayZoom);
    return viewBoxToString(
      applyZoomToViewBox(fit, z, 0.5, 0.5, fit, undefined, 8)
    );
  }, [viewBoxOverride, schemeDisplayZoom]);

  // If overlay text exists, render text instead of molecule
  if (overlayText) {
    return (
      <OverlayTextSvg
        text={overlayText}
        width={width}
        height={height}
        fontFamily={REACTION_PREVIEW_FONT_FAMILY}
        horizontalAlign={overlayTextHorizontalAlign}
        position={overlayPosition}
        interactive={overlayInteractive && Boolean(onOverlayPositionChange)}
        onPositionChange={onOverlayPositionChange}
      />
    );
  }

  return (
    <MolecularSVGProvider
      workspace={workspace}
      svgWidth={width}
      svgHeight={height}
    >
      <SVGCanvas style={{ border: 'none' }} viewBoxOverride={resolvedViewBoxForCanvas} />
    </MolecularSVGProvider>
  );
}

export const SingleMoleculeExport = forwardRef<HTMLDivElement, SingleMoleculeExportProps>(
  ({
    config,
    viewBox,
    layoutViewBox,
    persistedViewBox,
    overlayText,
    overlayPosition = null,
    overlayTextHorizontalAlign = 'middle',
    onOverlayPositionChange,
    exportWidth = DEFAULT_MOLECULE_EXPORT_DIMENSIONS.width,
    exportHeight = DEFAULT_MOLECULE_EXPORT_DIMENSIONS.height,
    showMoleculeDropTarget = false,
    onMoleculeDragOver,
    onMoleculeDrop,
    onMoleculePaste,
    selectedMolecule = false,
    onMoleculeSelect,
    interactivePreview = false,
    onMoleculeViewBoxChange,
    onMoleculeZoomChange,
    onMoleculeMetricsChange,
    onMoleculeControlsReady,
    schemeDisplayZoom = 1,
    id: rootId,
    className: rootClassName,
  }, ref) => {
    const moleculeTapSelect = useTapClickSelectSkippingDrag(onMoleculeSelect);

    const stagePadding = MOLECULE_LAYOUT_BASE.padding;
    const moleculeWidth = Math.max(64, exportWidth - stagePadding * 2);
    const moleculeHeight = Math.max(64, exportHeight - stagePadding * 2);

    const atomScaleCompensation = useMemo(
      () =>
        getSingleMoleculeScaleCompensation(viewBox, {
          width: exportWidth,
          height: exportHeight,
        }),
      [viewBox, exportWidth, exportHeight]
    );

    const schemeZoomClamped = Math.min(
      SCHEME_DISPLAY_ZOOM_MAX,
      Math.max(SCHEME_DISPLAY_ZOOM_MIN, schemeDisplayZoom)
    );
    const dropTargetLabelFontPx = REACTION_LABEL_BASE_PX * schemeZoomClamped;

    return (
      <div
        ref={ref}
        id={rootId}
        className={rootClassName}
        style={{
          width: exportWidth,
          height: exportHeight,
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: exportWidth,
            height: exportHeight,
            overflow: 'hidden',
          }}
        >
          <div
            className={`preview-paste-slot preview-paste-slot--molecule${selectedMolecule ? ' preview-paste-slot--selected' : ''}`}
            style={{
              position: 'absolute',
              left: stagePadding,
              top: stagePadding,
              width: moleculeWidth,
              height: moleculeHeight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            tabIndex={0}
            onPointerDownCapture={moleculeTapSelect.onPointerDownCapture}
            onClick={moleculeTapSelect.onClickSkipDrag}
            onFocus={onMoleculeSelect}
            onDragOver={onMoleculeDragOver}
            onDrop={onMoleculeDrop}
            onPaste={onMoleculePaste}
          >
            {(config || overlayText) && (
              interactivePreview && config && layoutViewBox && !overlayText ? (
                <AtomScaleWrap compensation={atomScaleCompensation}>
                  <InteractiveMolecule
                    config={config}
                    svgWidth={moleculeWidth}
                    svgHeight={moleculeHeight}
                    layoutViewBox={layoutViewBox}
                    persistedViewBox={persistedViewBox ?? null}
                    schemeDisplayZoom={schemeDisplayZoom}
                    onViewBoxChange={onMoleculeViewBoxChange}
                    onZoomChange={onMoleculeZoomChange}
                    onRenderingMetrics={onMoleculeMetricsChange}
                    alignZoomControls="none"
                    ariaLabel="Molecule structure with zoom and pan controls"
                    onControlsReady={onMoleculeControlsReady}
                  />
                </AtomScaleWrap>
              ) : (
                <AtomScaleWrap compensation={atomScaleCompensation}>
                  <BorderlessMolecule
                    config={config}
                    width={moleculeWidth}
                    height={moleculeHeight}
                    viewBoxOverride={viewBox}
                    schemeDisplayZoom={schemeDisplayZoom}
                    overlayText={overlayText}
                    overlayPosition={overlayPosition}
                    overlayInteractive={interactivePreview && Boolean(overlayText)}
                    onOverlayPositionChange={onOverlayPositionChange}
                    overlayTextHorizontalAlign={overlayTextHorizontalAlign}
                  />
                </AtomScaleWrap>
              )
            )}
            {showMoleculeDropTarget && !config && !overlayText && (
              <div className="preview-slot-drop-target">
                <p
                  className="default-overlay-heading"
                  style={reactionPreviewDropTargetHeadingStyle(dropTargetLabelFontPx)}
                >
                  Edit Molecule
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

SingleMoleculeExport.displayName = 'SingleMoleculeExport';
